import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Clock, Users, ChefHat, Repeat, FileText, Plus, Minus, Sparkles, RefreshCw, UserPlus, X } from "lucide-react";
import api, { formatApiErrorDetail, planWeek } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BottomNav from "../components/BottomNav";
import { SpeechBubble } from "../components/MascotBubble";
import DishImage from "../components/DishImage";
import SideSuggestion from "../components/SideSuggestion";
import ShareWeek from "../components/ShareWeek";
import GuestSheet from "../components/GuestSheet";
import BusyNightSheet from "../components/BusyNightSheet";
import { eatTiming } from "../lib/eatTiming";
import ReportSheet, { ReportFlag } from "../components/ReportSheet";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const TASTE_SHORT = { simple: "Classic", middle: "Middle", adventurous: "Adventurous" };
const STYLES = [
  { key: "standard", label: "Standard" },
  { key: "big", label: "Big eaters" },
  { key: "leftovers", label: "Leftovers" },
  { key: "lots", label: "Lots" },
];

function weekDates(offset = 0) {
  const now = new Date(); now.setDate(now.getDate() + offset * 7);
  const dow = (now.getDay() + 6) % 7; // Monday=0
  const monday = new Date(now); monday.setDate(now.getDate() - dow);
  return DAYS.map((_, i) => { const d = new Date(monday); d.setDate(monday.getDate() + i); return d; });
}

export default function WeeklyPlan() {
  const { activeId, activeHousehold } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gen, setGen] = useState(false);
  const [swapping, setSwapping] = useState(false);
  const [params, setParams] = useSearchParams();
  const isNext = params.get("week") === "next";
  planWeek.value = isNext ? "next" : "current";
  useEffect(() => () => { planWeek.value = "current"; }, []);
  const setWeek = (w) => { setParams(w === "next" ? { week: "next" } : {}); setPlan(null); setLoading(true); };
  const dates = weekDates(isNext ? 1 : 0);
  const todayName = isNext ? "" : DAY_NAMES[new Date().getDay()];
  const [selected, setSelected] = useState(DAYS.includes(todayName) ? todayName : "Monday");
  useEffect(() => { setSelected(DAYS.includes(todayName) ? todayName : "Monday"); }, [todayName]);
  const cur = activeHousehold?.currency || "A$";

  const load = useCallback(async () => {
    if (!activeId) return;
    setLoading(true);
    try { const { data } = await api.get(`/plan/${activeId}`); setPlan(data); setBusyEvents(data?.busy_events || []); }
    finally { setLoading(false); }
  }, [activeId, isNext]);
  const [busyEvents, setBusyEvents] = useState([]);
  const [busyOpen, setBusyOpen] = useState(false);
  const busyEvent = (idx) => {
    const d = dates[idx];
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return busyEvents.filter((e) => e.date === iso);
  };
  const busyLabel = (idx) => {
    const evs = busyEvent(idx);
    if (!evs.length) return "fast";
    const [ev] = evs;
    return <>{ev.title}<br />{ev.start} – {ev.end}{evs.length > 1 && <span data-testid="plan-busy-more"> · +{evs.length - 1} more</span>}</>;
  };
  const millieLine = (idx) => {
    const [ev] = busyEvent(idx);
    if (!ev) return "Quick one tonight — you've got this, legend!";
    const t = eatTiming(ev);
    return `${ev.title} at ${ev.start} — ${t ? t.millie : "quick one tonight, you've got this, legend!"}`;
  };
  const toggleBusy = async () => {
    const next = !dayPlan?.busy;
    const needsSwap = next && (dayPlan?.meal?.prep_time_min || 0) > 20;
    const t = needsSwap ? toast.loading(`Marking ${selected} busy — Millie's finding something quicker…`) : null;
    try {
      const { data } = await api.put("/plan/busy", { household_id: activeId, day: selected, busy: next });
      setPlan(data);
      if (t) toast.dismiss(t);
      if (!next) toast(`${selected} back to a normal night`);
      else if (data.swapped_from) toast.success(`${selected} sorted — swapped ${data.swapped_from} for ${data.new_title} (${data.new_prep} min)`);
      else toast.success(`${selected} marked busy — Millie will keep it quick`);
    } catch (err) { if (t) toast.dismiss(t); toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGen(true);
    toast.loading("Re-planning your week, hang tight…", { id: "gen" });
    try { const { data } = await api.post("/plan/generate", { household_id: activeId }); setPlan(data); toast.success("Fresh week — sorted!", { id: "gen" }); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail), { id: "gen" }); }
    finally { setGen(false); }
  };

  const swap = async () => {
    setSwapping(true);
    try { const { data } = await api.post("/plan/swap", { household_id: activeId, day: selected }); setPlan(data); toast.success(`${selected} swapped!`); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSwapping(false); }
  };

  const setPortion = async (body) => {
    try { const { data } = await api.put("/plan/portion", { household_id: activeId, day: selected, ...body }); setPlan(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const [guestOpen, setGuestOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const removeGuest = async (name) => {
    try { const { data } = await api.delete("/plan/guest", { data: { household_id: activeId, day: selected, name } }); setPlan(data); toast.success(`${name} taken off ${selected}'s table`); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const dayPlan = plan?.days?.find((d) => d.day === selected);
  const meal = dayPlan?.meal;
  const selIdx = DAYS.indexOf(selected);

  if (!loading && !plan) {
    return (
      <div className="min-h-screen w-full bg-[#F7E9E6] flex justify-center">
        <div className="relative w-full max-w-md min-h-screen bg-background pb-28 flex flex-col items-center justify-center text-center px-8 gap-4">
          <Sparkles size={40} strokeWidth={2} className="text-sorted-rose" />
          <p className="font-hand text-2xl text-sorted-ink">{isNext ? "Next week's not planned yet. Want me to sort it so your list's ready before the shop?" : "No week planned yet. Want me to sort it?"}</p>
          <button onClick={generate} disabled={gen} data-testid="plan-generate-empty"
            className="bg-sorted-rose text-white font-bold px-6 py-4 rounded-full flex items-center gap-2 shadow-soft">
            <Sparkles size={18} strokeWidth={2.5} /> {gen ? "Sorting…" : isNext ? "Plan next week" : "Plan my week"}
          </button>
          {isNext && <button onClick={() => setWeek("current")} data-testid="plan-week-back-current" className="text-sm font-bold text-sorted-slate">Back to this week</button>}
        </div>
      <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#F7E9E6] flex justify-center">
      <div className="relative w-full max-w-md min-h-screen bg-gradient-to-b from-[#FFF6F1] to-[#FCE7EC] pb-28">
        {/* Header */}
        <div className="px-6 pt-12 flex items-center justify-between">
          <button onClick={() => navigate("/home")} data-testid="plan-back" className="w-11 h-11 rounded-full bg-white/80 border-2 border-white flex items-center justify-center text-sorted-ink shadow-soft">
            <ArrowLeft size={19} strokeWidth={2.5} />
          </button>
          <h1 className="font-hand text-4xl text-sorted-ink">{isNext ? "Next week" : "Your week"}</h1>
          <button onClick={generate} disabled={gen} data-testid="plan-regenerate" className="w-11 h-11 rounded-full bg-white/80 border-2 border-white flex items-center justify-center text-sorted-ink shadow-soft">
            <RefreshCw size={17} strokeWidth={2.5} className={gen ? "animate-spin" : ""} />
          </button>
        </div>
        <div className="mx-6 mt-3 bg-white rounded-full p-1 flex border-2 border-[#FDECEF]" data-testid="plan-week-toggle">
          {[["current", "This week"], ["next", "Next week"]].map(([k, l]) => (
            <button key={k} onClick={() => setWeek(k)} data-testid={`plan-week-${k}`}
              className={`flex-1 py-2 rounded-full text-xs font-bold transition-colors ${(k === "next") === isNext ? "bg-sorted-ink text-white" : "text-sorted-slate"}`}>{l}</button>
          ))}
        </div>

        {/* Day chips */}
        <div className="mt-4 px-4 flex gap-2 overflow-x-auto no-scrollbar">
          {DAYS.map((day, i) => {
            const on = day === selected;
            const isToday = day === todayName;
            return (
              <button key={day} onClick={() => setSelected(day)} data-testid={`day-chip-${day}`}
                className={`shrink-0 w-14 rounded-2xl py-2 flex flex-col items-center border-2 transition-colors ${on ? "bg-sorted-rose border-sorted-rose text-white" : "bg-white border-[#FDECEF] text-sorted-ink"}`}>
                <span className={`text-[11px] font-bold ${on ? "text-white/90" : "text-sorted-slate"}`}>{day.slice(0, 3)}</span>
                <span className="font-heading font-bold text-lg leading-none mt-0.5">{dates[i].getDate()}</span>
                <span className={`text-[10px] ${on ? "text-white/90" : "text-sorted-slate"}`}>{MONTHS[dates[i].getMonth()]}</span>
                <span className={`w-1.5 h-1.5 rounded-full mt-1 ${isToday ? (on ? "bg-white" : "bg-sorted-rose") : "bg-emerald-400"}`} />
              </button>
            );
          })}
        </div>

        {/* Selected day detail */}
        {loading ? (
          <div className="px-6 mt-6"><div className="h-96 rounded-3xl bg-white/60 animate-pulse" /></div>
        ) : !meal ? (
          <div className="px-6 mt-6">
            <div className="bg-white rounded-3xl border-2 border-[#FDECEF] p-6 text-center shadow-soft" data-testid="plan-night-off">
              <p className="font-hand text-3xl text-sorted-ink">{selected} — night off!</p>
              <p className="text-sm text-sorted-slate mt-2">You asked for {plan.days.length} dinners this week, so {selected} is a free one. Leftovers, takeaway, or hit SOS if you get stuck.</p>
              <button onClick={() => navigate("/sos")} className="mt-4 bg-sorted-rose text-white font-bold px-5 py-3 rounded-full">SOS Dinner</button>
            </div>
            <ExtraMeals plan={plan} cur={cur} />
          </div>
        ) : (
          <div className="px-6 mt-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-hand text-3xl text-sorted-ink leading-none">{selected === todayName ? "Today" : selected}</p>
                <p className="text-sm font-semibold text-sorted-slate">{dates[selIdx].getDate()} {MONTHS[dates[selIdx].getMonth()]}</p>
              </div>
              <div className="flex flex-col items-end gap-1 max-w-[55%]">
                {dayPlan.busy ? (
                  <button onClick={() => setBusyOpen(true)} className="text-[11px] font-bold bg-warning/15 text-warning px-2.5 py-1 rounded-full text-right active:scale-95 transition-transform" data-testid="plan-busy-badge">Busy night · {busyLabel(selIdx)}</button>
                ) : !dayPlan.night_off && (
                  <button onClick={toggleBusy} className="text-[11px] font-bold bg-white/70 text-sorted-slate border border-[#FDECEF] px-2.5 py-1 rounded-full active:scale-95 transition-transform" data-testid="plan-mark-busy">+ Mark busy</button>
                )}
                {dayPlan.busy && busyEvent(selIdx)[0] && eatTiming(busyEvent(selIdx)[0]) && (
                  <span className="text-[11px] font-bold text-sorted-ink bg-sorted-sage/50 px-2.5 py-1 rounded-full text-right" data-testid="plan-eat-timing">{eatTiming(busyEvent(selIdx)[0]).label} · {eatTiming(busyEvent(selIdx)[0]).sub}</span>
                )}
                {dayPlan.busy && !busyEvent(selIdx).length && (
                  <button onClick={toggleBusy} className="text-[11px] font-bold text-sorted-slate underline" data-testid="plan-unmark-busy">Not busy after all</button>
                )}
              </div>
              {dayPlan.night_off && <span className="text-[11px] font-bold bg-[#DEE7F5] text-blue-700 px-2.5 py-1 rounded-full" data-testid="plan-night-off-badge">Night off 🌙</span>}
            </div>

            <AnimatePresence mode="wait">
              <motion.div key={meal.title} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-3">
                <div className="relative">
                  <DishImage meal={meal} className="w-full h-52 rounded-3xl" testId="plan-dish-image" />
                  <div className="absolute -bottom-3 right-4">
                    <SpeechBubble tail="br" className="max-w-[180px]" testId="plan-millie-bubble">
                      {dayPlan.busy ? millieLine(selIdx) : "Ripper choice — quick, easy and the whole fam will love it. ❤️"}
                    </SpeechBubble>
                  </div>
                </div>

                <h2 className="font-heading text-2xl font-semibold text-sorted-ink mt-4" data-testid="plan-meal-title">{meal.title}</h2>
                <div className="flex items-center gap-4 mt-2 text-sm font-bold text-sorted-slate">
                  <span className="flex items-center gap-1"><Clock size={15} strokeWidth={2.5} />{meal.prep_time_min} min</span>
                  <span className="flex items-center gap-1"><Users size={15} strokeWidth={2.5} />Serves {meal.servings}</span>
                  <span className="flex items-center gap-1"><ChefHat size={15} strokeWidth={2.5} />{meal.difficulty}</span>
                  <span className="ml-auto text-sorted-ink">{cur}{Number(meal.cost_estimate).toFixed(2)}</span>
                </div>
                {meal.description && <p className="text-sm text-sorted-slate mt-2 leading-relaxed">{meal.description}</p>}

                <SideSuggestion activeId={activeId} day={selected} meal={meal} onUpdated={setPlan} />

                <div className="flex flex-wrap gap-2 mt-3">
                  {meal.kid_friendly && <Tag>Kid friendly</Tag>}
                  <Tag>Budget friendly</Tag>
                  {meal.uses_pantry && <Tag>Uses pantry items</Tag>}
                </div>

                {/* Portion controls */}
                <div className="mt-4 bg-white rounded-3xl border-2 border-[#FDECEF] p-4 shadow-soft" data-testid="portion-controls">
                  <div className="flex items-center justify-between">
                    <span className="font-heading font-semibold text-sorted-ink">Serves</span>
                    <div className="flex items-center gap-3">
                      <button onClick={() => setPortion({ servings: meal.servings - 1 })} data-testid="portion-minus" aria-label="Fewer" className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sorted-ink"><Minus size={15} strokeWidth={3} /></button>
                      <span className="font-heading font-bold text-lg w-6 text-center" data-testid="portion-count">{meal.servings}</span>
                      <button onClick={() => setPortion({ servings: meal.servings + 1 })} data-testid="portion-plus" aria-label="More" className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-sorted-ink"><Plus size={15} strokeWidth={3} /></button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {STYLES.map((s) => (
                      <button key={s.key} onClick={() => setPortion({ portion_style: s.key })} data-testid={`portion-style-${s.key}`}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-colors ${meal.portion_style === s.key ? "bg-sorted-rose border-sorted-rose text-white" : "bg-white border-[#FDECEF] text-sorted-slate"}`}>
                        {s.label}
                      </button>
                    ))}
                    <button onClick={() => setGuestOpen(true)} data-testid="portion-guest"
                      className="px-3 py-1.5 rounded-full text-xs font-bold border-2 bg-white border-[#FDECEF] text-sorted-ink flex items-center gap-1">
                      <UserPlus size={13} strokeWidth={2.5} /> +Guest
                    </button>
                  </div>
                  {dayPlan.guests?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5" data-testid="guest-chips">
                      {dayPlan.guests.map((g) => (
                        <span key={g.name} className="inline-flex items-center gap-1 bg-sorted-peach/60 text-sorted-ink text-xs font-bold rounded-full pl-3 pr-1 py-1" data-testid={`guest-chip-${g.name}`}>
                          {g.name} · {TASTE_SHORT[g.taste] || "Middle"}
                          <button onClick={() => removeGuest(g.name)} aria-label={`Remove ${g.name}`} data-testid={`guest-remove-${g.name}`} className="w-5 h-5 rounded-full bg-white/80 flex items-center justify-center"><X size={11} strokeWidth={3} /></button>
                        </span>
                      ))}
                    </div>
                  )}
                  {dayPlan.guest_note && (
                    <p className="mt-2 text-xs bg-sorted-blush/50 text-sorted-ink rounded-2xl px-3 py-2 font-medium" data-testid="guest-note">🍷 {dayPlan.guest_note}</p>
                  )}
                  {meal.leftover_note && (
                    <p className="mt-3 text-xs bg-sorted-sage/50 text-sorted-ink rounded-2xl px-3 py-2 font-medium">🥡 {meal.leftover_note}</p>
                  )}
                  {meal.classic_twist && (
                    <p className="mt-2 text-xs bg-sorted-peach/40 text-sorted-ink rounded-2xl px-3 py-2 font-medium" data-testid={`classic-twist-${selected}`}>🍽️ Classic twist: {meal.classic_twist}</p>
                  )}
                </div>

                <button onClick={() => navigate(`/cook/${selected}`)} data-testid="plan-start-cooking"
                  className="mt-4 w-full bg-sorted-rose hover:bg-sorted-roseHover text-white font-bold py-4 rounded-full transition-colors">
                  Cook with Millie
                </button>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  <ActionBtn onClick={() => navigate("/recipes")} icon={FileText} label="View recipe" testId="plan-view-recipe" />
                  <ActionBtn onClick={swap} icon={Repeat} label={swapping ? "Swapping…" : "Swap meal"} testId="plan-swap" spin={swapping} />
                  <ActionBtn onClick={() => navigate("/kitchen")} icon={Plus} label={meal.side ? "Side added" : "Sides from kitchen"} testId="plan-add-side" />
                </div>
                <div className="mt-3 flex justify-end"><ReportFlag onClick={() => setReportOpen(true)} testId="plan-report-meal" /></div>
              </motion.div>
            </AnimatePresence>

            {/* This week at a glance */}
            <div className="mt-8 flex items-center justify-between">
              <h3 className="font-hand text-2xl text-sorted-ink">This week at a glance</h3>
              <button onClick={generate} className="text-sm font-bold text-sorted-rose">Edit week</button>
            </div>
            <ShareWeek activeId={activeId} />
            <div className="mt-3 flex gap-3 overflow-x-auto no-scrollbar pb-2">
              {plan.days.map((d, i) => (
                <button key={d.day} onClick={() => setSelected(d.day)} data-testid={`glance-${d.day}`}
                  className={`shrink-0 w-28 bg-white rounded-3xl border-2 p-2 text-left shadow-soft ${d.day === selected ? "border-sorted-rose" : "border-[#FDECEF]"}`}>
                  <DishImage meal={d.meal} className="w-full h-16 rounded-2xl" testId={`plan-thumb-${d.day}`} />
                  <p className="text-[11px] font-bold text-sorted-slate mt-1">{d.day.slice(0, 3)} {dates[i].getDate()}</p>
                  <p className="text-xs font-semibold text-sorted-ink leading-tight line-clamp-2">{d.meal.title}</p>
                  <p className="text-[10px] font-bold text-sorted-slate mt-0.5 flex items-center gap-0.5"><Clock size={10} strokeWidth={2.5} />{d.meal.prep_time_min} min</p>
                </button>
              ))}
            </div>
            <ExtraMeals plan={plan} cur={cur} />
          </div>
        )}
      </div>
        <ReportSheet open={reportOpen} onClose={() => setReportOpen(false)} category="meal" title="Report this meal" context={{ label: meal?.title, day: selected, meal_title: meal?.title }} />
        <BusyNightSheet open={busyOpen} onClose={() => setBusyOpen(false)} day={selected} dateLabel={`${dates[selIdx].getDate()} ${MONTHS[dates[selIdx].getMonth()]}`} events={busyEvent(selIdx)} />
        <GuestSheet open={guestOpen} onClose={() => setGuestOpen(false)} activeId={activeId} day={selected} onPlan={setPlan} />
      <BottomNav />
    </div>
  );
}

function ExtraMeals({ plan, cur }) {
  const groups = [["Lunches", plan.lunches], ["Brekkie", plan.breakfasts]].filter(([, m]) => m?.length);
  if (!groups.length) return null;
  return (
    <div className="mt-8 space-y-6" data-testid="extra-meals">
      {groups.map(([label, meals]) => (
        <div key={label}>
          <h3 className="font-hand text-2xl text-sorted-ink">{label} this week</h3>
          <div className="mt-3 space-y-2">
            {meals.map((m, i) => (
              <div key={i} className="bg-white rounded-3xl border-2 border-[#FDECEF] p-3 flex items-center gap-3 shadow-soft" data-testid={`extra-${label.toLowerCase()}-${i}`}>
                <DishImage meal={m} className="w-14 h-14 rounded-2xl shrink-0" testId={`extra-image-${i}`} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sorted-ink leading-tight">{m.title}</p>
                  <p className="text-xs text-sorted-slate truncate">{m.description}</p>
                </div>
                <span className="text-xs font-bold text-sorted-slate shrink-0">{m.prep_time_min} min · {cur}{Number(m.cost_estimate).toFixed(0)}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const Tag = ({ children }) => (
  <span className="bg-sorted-blush text-sorted-ink text-xs font-bold px-3 py-1.5 rounded-full">{children}</span>
);

function ActionBtn({ onClick, icon: Icon, label, testId, spin }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="bg-white border-2 border-[#FDECEF] rounded-2xl py-3 flex flex-col items-center gap-1 text-sorted-ink shadow-soft">
      <Icon size={18} strokeWidth={2.5} className={spin ? "animate-spin" : ""} />
      <span className="text-[11px] font-bold">{label}</span>
    </button>
  );
}
